
import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DocumentProps } from "@/hooks/admin/identity-docs/useSubmittedDocuments";
import { format } from "date-fns";
import { StatusBadge } from "./table/StatusBadge";
import { DocumentActionsCell } from "./table/actions/DocumentActionsCell";
import { DocumentMetadata } from "./table/DocumentMetadata";
import { useIsMobile } from "@/hooks/use-mobile";
import { Card, CardContent } from "@/components/ui/card";

interface SubmittedDocsTableProps {
  documents: DocumentProps[];
  onDocumentUpdated?: () => void;
}

export function SubmittedDocsTable({ documents, onDocumentUpdated }: SubmittedDocsTableProps) {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const isMobile = useIsMobile();
  
  const toggleRow = (id: string) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  
  if (documents.length === 0) {
    return (
      <div className="text-center py-10 border rounded-md">
        <p className="text-muted-foreground">No documents found</p>
      </div>
    );
  }

  // Mobile card layout
  if (isMobile) {
    return (
      <div className="space-y-4">
        {documents.map((document) => (
          <Card key={document.id} className="w-full">
            <CardContent className="p-4 space-y-3">
              <div className="flex justify-between items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">
                    {document.profiles.first_name} {document.profiles.last_name}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {document.email}
                  </div>
                </div>
                <StatusBadge status={document.verification_status} />
              </div>
              
              <div className="space-y-2">
                <div>
                  <div className="text-sm font-medium">{document.id_document_types.document_name}</div>
                  <DocumentMetadata document={document} />
                </div>
                
                <div className="text-xs text-muted-foreground">
                  Submitted: {format(new Date(document.submitted_at), "MMM d, yyyy")}
                </div>
                
                {document.notes && (
                  <div className="text-xs text-muted-foreground truncate">
                    Notes: {document.notes}
                  </div>
                )}
              </div>
              
              <div className="flex justify-end pt-2">
                <DocumentActionsCell 
                  document={document} 
                  onDocumentUpdated={onDocumentUpdated} 
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Desktop table layout
  return (
    <div className="rounded-md border overflow-hidden">
      <Table className="w-full">
        <TableHeader className="bg-muted/50">
          <TableRow>
            <TableHead className="w-[200px]">User</TableHead>
            <TableHead>Document Type</TableHead>
            <TableHead className="hidden md:table-cell">Submitted</TableHead>
            <TableHead className="hidden md:table-cell">Details</TableHead>
            <TableHead className="w-[100px] text-center">Status</TableHead>
            <TableHead className="w-[70px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {documents.map((document) => (
            <TableRow
              key={document.id}
              onClick={() => toggleRow(document.id)}
              className="cursor-pointer hover:bg-muted/30"
            >
              <TableCell className="font-medium">
                {document.profiles.first_name} {document.profiles.last_name}
                <div className="text-muted-foreground text-xs truncate max-w-[180px]">
                  {document.email}
                </div>
              </TableCell>
              <TableCell>
                <div className="font-medium">{document.id_document_types.document_name}</div>
                <DocumentMetadata document={document} />
              </TableCell>
              <TableCell className="hidden md:table-cell">
                {format(new Date(document.submitted_at), "MMM d, yyyy")}
              </TableCell>
              <TableCell className="hidden md:table-cell text-muted-foreground text-sm">
                {document.notes || "No notes"}
              </TableCell>
              <TableCell className="text-center">
                <StatusBadge status={document.verification_status} />
              </TableCell>
              <TableCell onClick={(e) => e.stopPropagation()}>
                <DocumentActionsCell 
                  document={document} 
                  onDocumentUpdated={onDocumentUpdated} 
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
